import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, MenuItem, Select, CircularProgress } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Group_Type, Field_Type } from "../constants/enum";
import { toast } from "react-toastify";
import { getFieldTypeLabel } from "../utils/fieldTypes";
import { useAddFeatureGroupMutation, useUpdateFeatureGroupMutation, useGetFeatureGroupDetailsQuery } from "../service/features/api";

interface AddSubGroupProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  groupId?: string;
  featureData?: any;
}

const AddFeatureSubGroup: React.FC<AddSubGroupProps> = ({ open, setOpen, groupId, featureData }) => {
  const [name, setName] = useState<string>("");
  const [fieldType, setFieldType] = useState<number | "">("");
  const [groupType, setGroupType] = useState<number | "">("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: parentData } = useGetFeatureGroupDetailsQuery(groupId, { skip: !groupId });
  const [addFeatureGroup, { isLoading: isAdding }] = useAddFeatureGroupMutation();
  const [updateFeatureGroup, { isLoading: isUpdating }] = useUpdateFeatureGroupMutation();

  useEffect(() => {
    if (open) {
      if (featureData) {
        setName(featureData.name || "");
        setFieldType(featureData.fieldType || parentData?.data?.fieldType || "");
        setGroupType(featureData.groupType || parentData?.data?.groupType || "");
        setErrors({});
      } else {
        setName("");
        setFieldType(parentData?.data?.fieldType || "");
        setGroupType(parentData?.data?.groupType || "");
        setErrors({});
      }
    }
  }, [open, featureData, parentData]);

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "Name is required";
    else if (name.length > 40) newErrors.name = "Name cannot exceed 40 characters";

    if (groupType === "") newErrors.groupType = "Group Type is required";
    if (fieldType === "") newErrors.fieldType = "Field Type is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields");
      return;
    }

    // Prepare payload following user's Postman example structure
    const payload: any = {
      name,
      fieldType: Number(fieldType),
      groupType: Number(groupType),
      type: featureData?.type || parentData?.data?.type || 1, // Default to 1 from example if not found
      parentFeatureId: groupId,
      categoryId: featureData?.categoryId || parentData?.data?.categoryId || [],
      order: featureData?.order || parentData?.data?.order || 1,
    };

    try {
      let res;
      if (featureData?._id) {
        res = await updateFeatureGroup({ id: featureData._id, ...payload }).unwrap();
        toast.success(res?.message || "Sub-feature updated successfully");
      } else {
        res = await addFeatureGroup(payload).unwrap();
        toast.success(res?.message || "Sub-feature added successfully");
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={() => setOpen(false)}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{featureData ? "Edit Sub-Feature" : "Add Sub-Feature"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                setErrors({});
                setOpen(false);
              }}
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <div className="control_group">
              <label>Sub-Feature Name</label>
              <TextField
                fullWidth
                hiddenLabel
                placeholder="Enter Sub-Feature Name"
                value={name}
                onChange={(e) => {
                  if (e.target.value.length <= 40) {
                    setName(e.target.value);
                    if (errors.name) setErrors(prev => ({ ...prev, name: "" }));
                  }
                }}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 40 }}
              />
            </div>

            <div className="control_group w_100">
              <label>Group Type</label>
              <Select
                fullWidth
                displayEmpty
                disabled
                value={groupType}
                onChange={(e) => {
                  setGroupType(Number(e.target.value));
                  if (errors.groupType) setErrors(prev => ({ ...prev, groupType: "" }));
                }}
                error={!!errors.groupType}
              >
                <MenuItem value="" disabled>Select Group Type</MenuItem>
                {Object.entries(Group_Type)
                  .filter(([key]) => isNaN(Number(key)))
                  .map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                      {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                    </MenuItem>
                  ))}
              </Select>
              {errors.groupType && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.groupType}</p>}
            </div>

            <div className="control_group w_100">
              <label htmlFor="type">Field Type</label>
              <Select
                fullWidth
                displayEmpty
                disabled
                value={fieldType}
                onChange={(e) => {
                  setFieldType(Number(e.target.value));
                  if (errors.fieldType) setErrors(prev => ({ ...prev, fieldType: "" }));
                }}
                error={!!errors.fieldType}
              >
                <MenuItem value="" disabled>Select Field Type</MenuItem>
                {Object.entries(Field_Type)
                  .filter(([key]) => isNaN(Number(key)))
                  .map(([key, value]) => (
                    <MenuItem key={value} value={value}>{getFieldTypeLabel(value)}</MenuItem>
                  ))}
              </Select>
              {errors.fieldType && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.fieldType}</p>}
            </div>

            <div className="footer_btn_flex">
              <Button
                className="btn btn_primary"
                onClick={handleSave}
                disabled={isAdding || isUpdating}
              >
                {isAdding || isUpdating ? <CircularProgress size={24} color="inherit" /> : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default AddFeatureSubGroup;
